/**
 * DocumentViewerModal - THE document, and only the document.
 *
 * Streams the decrypted binary from the backend (no SAS URL is ever exposed),
 * converts DOCX to HTML, injects an anchor per finding, and renders it. That
 * is the whole of its job.
 *
 * IT NO LONGER DRAWS FINDINGS. It used to carry a 288px sidebar of finding
 * cards with their own accept/reject, which meant a reviewer could be reading
 * the same finding in two places at once - the workspace canvas and this
 * sidebar - with two implementations of the decision drifting apart. The
 * findings pane is now the ONE place a finding is read and decided; what this
 * component contributes to that is `onFindingSelect`, fired when a reader
 * clicks the clause itself.
 *
 * Three viewing modes:
 *  - Original: the uploaded document as-is
 *  - Revised (Clean): accepted/modified finding decisions applied, no markup
 *  - Revised (Redline): struck-out original + highlighted replacement text
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiFileTextLight,
  PiDownloadSimpleLight,
  PiArrowsClockwiseLight,
} from 'react-icons/pi';

import {
  fetchDocumentContent,
  streamReviewDocument,
  streamRevisedDocument,
} from '../../store/actions/Review/ReviewActions';
import { FindingOverlay } from '../../types/review';
import { RevisionStyle, ViewSource, supportsRevision as fileSupportsRevision } from './documentView';
import { extractHtmlFromDocx } from '../../utils/docxHandler';
import DOMPurify from 'dompurify';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, documentViewerTourSteps } from '../shared/FeatureTour';

// ─── Props ──────────────────────────────────────────────────────────────────

interface DocumentViewerModalProps {
  sessionUuid: string;
  documentId: number;
  documentName: string;
  scrollToFindingUuid?: string;
  scrollSearchText?: string;
  scrollSectionReference?: string;
  /**
   * Bumped when the SAME finding is asked for again.
   *
   * The scroll fires on a change of target, and re-asking for a finding you
   * are already on is a real request - the highlight fades after a moment, so
   * "show me that clause again" has to mean something.
   */
  scrollNonce?: number;
  // Controlled header props (from the session's document toolbar)
  hideHeader?: boolean;
  viewSource?: ViewSource;
  revisionStyle?: RevisionStyle;
  /**
   * Changes whenever a decision changes, so the revised bytes are re-fetched.
   *
   * The revised document is SERVER-RENDERED from the accepted findings, so it
   * is stale the instant one is accepted, rejected or undone. Nothing in
   * `sessionUuid` / `documentId` / `revisionStyle` moves when that happens —
   * which is why the view sat on pre-decision text until a manual reload.
   */
  revisionToken?: string;
  onRevisionStyleChange?: (s: RevisionStyle) => void;
  /**
   * The reader's text size, in px for the document's root.
   *
   * Pushed by message rather than baked into `srcDoc`, because rebuilding
   * that string reloads the iframe - the document would flash and lose its
   * scroll position on every press of the zoom button.
   */
  readingScalePx?: number;
  /**
   * A finding was clicked IN THE DOCUMENT.
   *
   * The anchors this component injects are the only place a reader can point
   * at a clause and mean a finding, so the page's selection is driven from
   * here. The viewer itself shows no finding UI - it says which one was
   * touched and lets the findings pane answer.
   */
  onFindingSelect?: (findingUuid: string) => void;
}

const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
  sessionUuid,
  documentId,
  documentName,
  scrollToFindingUuid,
  scrollSearchText,
  scrollSectionReference,
  scrollNonce,
  readingScalePx,
  hideHeader = false,
  viewSource: viewSourceProp,
  revisionStyle: revisionStyleProp,
  revisionToken = '',
  onRevisionStyleChange,
  onFindingSelect,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const documentContent = useSelector((s: RootState) => s.review.documentContent);
  const isLoading = useSelector((s: RootState) => s.review.isLoadingDocument);

  /*
    The overlay - a POSITIONING payload, and nothing more.

    It carries what anchoring a finding in the page needs: the uuid, the
    section, the text to match. It used to be joined to the session's richer
    findings so the viewer could draw finding cards of its own; the findings
    pane draws those now, from the store, and the join went with them.

    MEMOISED because the render effect below depends on it: `|| []` alone
    hands back a new array every render, and a render effect keyed on a fresh
    array every render is an infinite loop.
  */
  const findings: FindingOverlay[] = useMemo(
    () => documentContent?.findings_overlay || [],
    [documentContent?.findings_overlay],
  );

  const [iframeLoaded, setIframeLoaded] = useState(false);
  const pendingScrollRef = useRef<{ findingUuid: string; searchText: string; sectionReference: string; targetSubsection: string } | null>(null);
  /*
    The LAST place this viewer was asked to look.

    Accepting a finding re-renders the document from the server, which swaps
    the html and remounts the iframe - and the reload used to land the reader
    back at the top of a document they were reading in the middle of, with
    nothing marked. So the target is remembered, and re-fired at the reloaded
    frame: the reviewer sees their accepted change, in place, highlighted.
  */
  const lastScrollRef = useRef<{ findingUuid: string; searchText: string; sectionReference: string; targetSubsection: string } | null>(null);

  // ── Original document state ──
  /* The bytes, kept: rendering them is a separate step that re-runs when the
     findings overlay arrives. See effects 2 and 2b. */
  const [originalBlob, setOriginalBlob] = useState<Blob | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [streamedContentType, setStreamedContentType] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamError, setStreamError] = useState(false);
  const [docxHtml, setDocxHtml] = useState<string | null>(null);

  // ── Viewing mode state - controlled by parent when hideHeader=true ──
  const [_viewSource, _setViewSource] = useState<ViewSource>('original');
  const [_revisionStyle, _setRevisionStyle] = useState<RevisionStyle>('clean');
  const viewSource = viewSourceProp ?? _viewSource;
  const revisionStyle = revisionStyleProp ?? _revisionStyle;
  const setViewSource = (s: ViewSource) => { if (viewSourceProp === undefined) _setViewSource(s); };

  /*
    What was last fetched - the STYLE AND THE DECISIONS BEHIND IT.

    This was the style alone, and it is the whole bug: once `clean` had been
    fetched, `lastFetchedStyle.current === revisionStyle` was true forever, so
    accepting a finding could not re-fetch anything. The only thing that reset
    it was the user toggling the style by hand, which is exactly the "refresh
    to see your own edit" the reviewer was doing.

    Keyed on both now, so a decision invalidates the cached bytes while
    flipping back and forth between clean and redline still costs nothing when
    nothing has changed.
  */
  const lastFetchedKey = useRef<string | null>(null);

  const setRevisionStyle = (s: RevisionStyle) => {
    if (revisionStyleProp === undefined) _setRevisionStyle(s);
    lastFetchedKey.current = null;
    onRevisionStyleChange?.(s);
  };

  // ── Revised document state ──
  const [revisedBlob, setRevisedBlob] = useState<Blob | null>(null);
  const [revisedBlobUrl, setRevisedBlobUrl] = useState<string | null>(null);
  const [revisedContentType, setRevisedContentType] = useState<string>('');
  const [revisedDocxHtml, setRevisedDocxHtml] = useState<string | null>(null);
  const [isStreamingRevised, setIsStreamingRevised] = useState(false);
  const [revisedError, setRevisedError] = useState(false);
  const [noRevisionAvailable, setNoRevisionAvailable] = useState(false);

  // Ref to the document iframe for scroll-to-finding
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Tour
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'document-viewer',
    steps: documentViewerTourSteps,
    readyToShow: !isLoading && (!!blobUrl || !!docxHtml),
    autoStartDelay: 600,
  });

  // 1. Fetch document metadata + findings overlay (JSON)
  useEffect(() => {
    dispatch(fetchDocumentContent(sessionUuid, documentId) as any);
  }, [dispatch, sessionUuid, documentId]);

  // ── Shared DOCX-to-HTML converter with finding anchors ──
  const convertBlobToHtml = useCallback(async (blob: Blob, findingsForAnchors?: FindingOverlay[]): Promise<string | null> => {
    const ct = (blob.type || '').toLowerCase();
    const isDocx =
      ct.includes('officedocument') ||
      ct.includes('msword') ||
      documentName.toLowerCase().endsWith('.docx') ||
      documentName.toLowerCase().endsWith('.doc');

    if (!isDocx) return null;

    const arrayBuffer = await blob.arrayBuffer();
    let html = await extractHtmlFromDocx(arrayBuffer);
    if (!html || html.trim().length === 0) return null;

    // Inject anchor markers - prefer target_subsection, fall back to word-based matching
    if (findingsForAnchors && findingsForAnchors.length > 0) {
      for (const f of findingsForAnchors) {
        // Strategy 1: target_subsection - match the paragraph starting with that section number
        if (f.target_subsection && !html.includes(`id="finding-${f.uuid}"`)) {
          const secNum = f.target_subsection.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // Match a section number at the start of text content (after potential HTML tags)
          const secPattern = `(>\\s*${secNum}[.\\s\\t])`;
          try {
            const secRegex = new RegExp(secPattern, 'i');
            const replaced = html.replace(secRegex, `><span id="finding-${f.uuid}" class="finding-anchor">${f.target_subsection} </span>`);
            if (replaced !== html) {
              html = replaced;
              continue; // Skip word-based matching
            }
          } catch { /* Skip */ }
        }

        // Strategy 2: word-based matching (existing logic)
        if (!f.original_text) continue;
        const words = f.original_text.trim().split(/\s+/).slice(0, 8);
        if (words.length < 2) continue;
        const wordPatterns = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        // Allow HTML tags + whitespace between words so <b>word</b> still matches
        const pattern = wordPatterns.join('(?:<[^>]*>|\\s)+');
        try {
          const regex = new RegExp(`(${pattern})`, 'i');
          html = html.replace(regex, `<span id="finding-${f.uuid}" class="finding-anchor">$1</span>`);
        } catch {
          // Skip findings whose text produces an invalid regex
        }
      }
    }

    // Sanitize the converted document body before it is embedded in the
    // same-origin srcDoc iframe. A malicious .docx could otherwise smuggle
    // <script>, on* handlers or javascript: URLs that would run in our origin
    // (XSS → token/session exposure). The template's own trusted click/scale
    // script is added AFTER this call, so it survives; only the untrusted body
    // is scrubbed. Anchor <span>s (id/class) and data: images are preserved.
    const safeBody = DOMPurify.sanitize(html, { USE_PROFILES: { html: true }, ADD_ATTR: ['target'] });

    return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  /* The reader's size. 15px is the default - one step under the browser's
     16, which read slightly large on a screen held further away than paper —
     and the parent overwrites this root value when the zoom changes. Every
     size below is relative, so the whole document scales from this one line
     without re-rendering. */
  html { font-size: 15px; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 1rem; padding: 2.6rem 3.6rem; max-width: 58rem; margin: 0 auto; line-height: 1.6; color: #1f2937; background: #ffffff; }
  table { border-collapse: collapse; width: 100%; margin: 1em 0; }
  td, th { border: 1px solid #d1d5db; padding: 8px; }
  img { max-width: 100%; height: auto; }
  del { color: #dc2626; text-decoration: line-through; background: #fef2f2; }
  ins, u { color: #16a34a; text-decoration: underline; background: #f0fdf4; }
  /* An anchor is a finding you can POINT AT. It is marked so a reader can
     see where the findings are without a sidebar listing them, and clicking
     one selects it in the findings pane. Champagne, matching the app. */
  .finding-anchor { scroll-margin-top: 20px; cursor: pointer;
    background: rgba(193,164,120,0.10); border-bottom: 1.5px solid rgba(193,164,120,0.5);
    border-radius: 2px; transition: background 0.2s ease; }
  .finding-anchor:hover { background: rgba(193,164,120,0.24); }
  .highlight { background: #fef3c7; border-radius: 2px; scroll-margin-top: 20px; transition: background 0.6s ease; }
  .highlight-fade { background: transparent; transition: background 1.5s ease; }
  html::-webkit-scrollbar { width: 6px; }
  html::-webkit-scrollbar-track { background: transparent; }
  html::-webkit-scrollbar-thumb { background: rgba(156,163,175,0.5); border-radius: 999px; }
  html::-webkit-scrollbar-thumb:hover { background: rgba(107,114,128,0.7); }
</style>
<script>
  function clearHighlights() {
    var els = document.querySelectorAll('.highlight, .highlight-fade');
    for (var i = 0; i < els.length; i++) {
      els[i].classList.remove('highlight', 'highlight-fade');
    }
  }
  function findSectionInDom(sectionRef) {
    if (!sectionRef) return null;
    var ref = sectionRef.replace(/\\s+/g, ' ').trim().toLowerCase();
    var numMatch = ref.match(/(\\d+(?:\\.\\d+)+)/);
    var sectionNum = numMatch ? numMatch[1] : null;
    var blocks = document.querySelectorAll('p, li, td, th, h1, h2, h3, h4, h5, h6, blockquote, dt, dd');
    if (sectionNum) {
      for (var i = 0; i < blocks.length; i++) {
        var bt = blocks[i].textContent.replace(/\\s+/g, ' ').trim();
        if (bt.length > 0 && (bt.indexOf(sectionNum + ' ') === 0 || bt.indexOf(sectionNum + '.') === 0 || bt.indexOf(sectionNum + '\\t') === 0 || bt === sectionNum)) {
          return blocks[i];
        }
      }
    }
    for (var i = 0; i < blocks.length; i++) {
      var bt = blocks[i].textContent.replace(/\\s+/g, ' ').trim().toLowerCase();
      if (bt.length > 0 && bt.indexOf(ref) !== -1 && bt.length < ref.length * 3) {
        return blocks[i];
      }
    }
    if (sectionNum) {
      for (var i = 0; i < blocks.length; i++) {
        var bt = blocks[i].textContent.replace(/\\s+/g, ' ').trim();
        if (bt.substring(0, 20).indexOf(sectionNum) !== -1) {
          return blocks[i];
        }
      }
    }
    return null;
  }
  function findTextInDom(text) {
    if (!text) return null;
    var norm = text.replace(/\\s+/g, ' ').trim().toLowerCase();
    var blocks = document.querySelectorAll('p, li, td, th, h1, h2, h3, h4, h5, h6, blockquote, dt, dd');
    var lengths = [80, 50, 25];
    for (var li = 0; li < lengths.length; li++) {
      var frag = norm.substring(0, Math.min(norm.length, lengths[li]));
      if (frag.length < 8) continue;
      for (var i = 0; i < blocks.length; i++) {
        var bt = blocks[i].textContent.replace(/\\s+/g, ' ').trim().toLowerCase();
        if (bt.length > 0 && bt.indexOf(frag) !== -1) {
          return blocks[i];
        }
      }
    }
    return null;
  }
  function highlightEl(el) {
    clearHighlights();
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('highlight');
    setTimeout(function() {
      el.classList.remove('highlight');
      el.classList.add('highlight-fade');
      setTimeout(function() { el.classList.remove('highlight-fade'); }, 1500);
    }, 1500);
  }
  function doScroll(data) {
    var el = document.getElementById('finding-' + data.findingUuid);
    if (!el && data.targetSubsection) el = findSectionInDom(data.targetSubsection);
    if (!el && data.sectionReference) el = findSectionInDom(data.sectionReference);
    if (!el && data.searchText) el = findTextInDom(data.searchText);
    if (el) { highlightEl(el); return true; }
    return false;
  }
  document.addEventListener('click', function(e) {
    var el = e.target;
    while (el && el !== document.body) {
      if (el.classList && el.classList.contains('finding-anchor')
          && el.id && el.id.indexOf('finding-') === 0) {
        parent.postMessage({ type: 'findingClicked', findingUuid: el.id.slice(8) }, '*');
        return;
      }
      el = el.parentNode;
    }
  });
  window.addEventListener('message', function(e) {
    if (e.data && e.data.type === 'setReadingScale') {
      document.documentElement.style.fontSize = e.data.px + 'px';
      return;
    }
    if (!e.data || e.data.type !== 'scrollToFinding') return;
    var data = e.data;
    if (!doScroll(data)) {
      setTimeout(function() { doScroll(data); }, 400);
    }
  });
</script>
</head><body>${safeBody}</body></html>`;
  }, [documentName]);

  /*
    2. FETCH the original document's bytes. Rendering them is step 2b.

    These were one step, and the anchors paid for it: converting needs the
    findings overlay, the overlay arrives from a DIFFERENT request (step 1),
    and the download almost always won the race - so `convertBlobToHtml` ran
    against the empty array captured on the first render and injected NO
    anchors at all. The document had no marked clauses, which was survivable
    while a sidebar listed the findings and fatal once clicking a clause
    became the way to open one.

    Fetching and rendering are separate now: the bytes are kept, and the
    render below re-runs when the overlay lands.
  */
  useEffect(() => {
    let cancelled = false;

    const streamDoc = async () => {
      setIsStreaming(true);
      setStreamError(false);
      const result = await dispatch(
        streamReviewDocument(sessionUuid, documentId) as any,
      );
      if (cancelled) return;

      if (result?.success && result.blob) {
        setStreamedContentType((result.contentType || '').toLowerCase());
        setOriginalBlob(result.blob);
      } else {
        setStreamError(true);
      }
      setIsStreaming(false);
    };

    streamDoc();
    return () => { cancelled = true; };
  }, [dispatch, sessionUuid, documentId]);

  /*
    2b. RENDER the bytes - again whenever the findings overlay changes.

    A DOCX becomes HTML with an anchor per finding; anything else becomes a
    blob URL for the native viewer. Re-running on `findings` is what puts the
    anchors in: the first pass may have had none to inject.
  */
  useEffect(() => {
    if (!originalBlob) return;
    let cancelled = false;

    (async () => {
      try {
        const html = await convertBlobToHtml(originalBlob, findings);
        if (cancelled) return;
        if (html) {
          setDocxHtml(html);
        } else {
          setBlobUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(originalBlob);
          });
        }
      } catch (err) {
        if (cancelled) return;
        console.error('DOCX conversion failed, falling back to download:', err);
        setBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(originalBlob);
        });
      }
    })();

    return () => { cancelled = true; };
  }, [originalBlob, findings, convertBlobToHtml]);

  // 3. Stream the revised document when in revised mode
  useEffect(() => {
    if (viewSource !== 'revised') return;
    const fetchKey = `${revisionStyle}:${revisionToken}`;
    if (lastFetchedKey.current === fetchKey) return; // same style, same decisions

    let cancelled = false;

    const streamRevised = async () => {
      setIsStreamingRevised(true);
      setRevisedError(false);
      setNoRevisionAvailable(false);

      // Clear the previous style's render before fetching the new one.
      setRevisedBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      setRevisedDocxHtml(null);
      setRevisedBlob(null);

      const result = await dispatch(
        streamRevisedDocument(sessionUuid, documentId, revisionStyle) as any,
      );
      if (cancelled) return;

      if (result?.noRevision) {
        setNoRevisionAvailable(true);
        setIsStreamingRevised(false);
        return;
      }

      if (result?.success && result.blob) {
        setRevisedContentType(result.contentType || '');
        setRevisedBlob(result.blob);
        lastFetchedKey.current = fetchKey;
      } else {
        setRevisedError(true);
      }
      setIsStreamingRevised(false);
    };

    streamRevised();
    return () => { cancelled = true; };
  }, [dispatch, sessionUuid, documentId, viewSource, revisionStyle, revisionToken]);

  /* 3b. Render the revised bytes - same split, same reason as 2b. */
  useEffect(() => {
    if (!revisedBlob) return;
    let cancelled = false;

    (async () => {
      try {
        const html = await convertBlobToHtml(revisedBlob, findings);
        if (cancelled) return;
        if (html) {
          setRevisedDocxHtml(html);
        } else {
          setRevisedBlobUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(revisedBlob);
          });
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Revised DOCX conversion failed:', err);
        setRevisedBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(revisedBlob);
        });
      }
    })();

    return () => { cancelled = true; };
  }, [revisedBlob, findings, convertBlobToHtml]);

  // Reset revision cache when switching back to original
  useEffect(() => {
    if (viewSource === 'original') {
      lastFetchedKey.current = null;
    }
  }, [viewSource]);

  /*
    Revoke the object URLs on UNMOUNT, and only then.

    Keyed on the urls, this cleanup ran on every change to them - revoking the
    url it had just been given a moment before it was rendered. The live urls
    are read through a ref so the effect can stay mount-only.
  */
  const liveUrls = useRef<{ blobUrl: string | null; revisedBlobUrl: string | null }>({
    blobUrl: null, revisedBlobUrl: null,
  });
  liveUrls.current = { blobUrl, revisedBlobUrl };
  useEffect(() => () => {
    if (liveUrls.current.blobUrl) URL.revokeObjectURL(liveUrls.current.blobUrl);
    if (liveUrls.current.revisedBlobUrl) URL.revokeObjectURL(liveUrls.current.revisedBlobUrl);
  }, []);

  // ── Active content for rendering (original vs revised) ──
  const activeBlobUrl = viewSource === 'revised' ? revisedBlobUrl : blobUrl;
  const activeDocxHtml = viewSource === 'revised' ? revisedDocxHtml : docxHtml;
  const activeContentType = viewSource === 'revised' ? revisedContentType : streamedContentType;
  const activeLoading = viewSource === 'revised' ? isStreamingRevised : isStreaming;
  const activeError = viewSource === 'revised' ? revisedError : streamError;

  // Determine rendering strategy from content-type / file_type
  const viewerType = useMemo(() => {
    if (activeDocxHtml) return 'docx' as const;
    if (!activeBlobUrl) return null;

    const fileType = (documentContent?.file_type || '').toLowerCase();
    const ct = activeContentType.toLowerCase();

    const isPdf = fileType.includes('pdf') || ct.includes('pdf');
    const isImage =
      fileType.includes('png') || fileType.includes('jpg') || fileType.includes('jpeg') ||
      ct.includes('image/');
    const isDocx =
      fileType.includes('doc') || fileType.includes('word') || fileType === 'docx' ||
      ct.includes('officedocument') || ct.includes('msword');
    const isText = fileType === 'txt' || fileType === 'csv' || ct.includes('text/');

    if (isPdf) return 'pdf' as const;
    if (isImage) return 'image' as const;
    if (isText) return 'text' as const;
    if (isDocx) return 'download' as const;
    return 'download' as const;
  }, [activeBlobUrl, activeDocxHtml, documentContent?.file_type, activeContentType]);

  const handleDownload = useCallback(() => {
    const url = activeBlobUrl || blobUrl;
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    const suffix = viewSource === 'revised' ? `_${revisionStyle}` : '';
    const base = documentName.replace(/(\.[^.]+)$/, `${suffix}$1`);
    a.download = base;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [activeBlobUrl, blobUrl, documentName, viewSource, revisionStyle]);

  // Scroll-to-finding: post message to iframe.
  // If iframe not yet loaded, store as pending and apply once it loads.
  const scrollToFinding = useCallback((findingUuid: string, searchText?: string, sectionReference?: string, targetSubsection?: string) => {
    const payload = {
      type: 'scrollToFinding' as const,
      findingUuid,
      searchText: searchText || '',
      sectionReference: sectionReference || '',
      targetSubsection: targetSubsection || '',
    };
    lastScrollRef.current = payload;
    const iframe = iframeRef.current;
    if (iframe?.contentWindow && iframeLoaded) {
      iframe.contentWindow.postMessage(payload, window.location.origin);
    } else {
      // Store for when iframe fires onLoad
      pendingScrollRef.current = payload;
    }
  }, [iframeLoaded]);

  // Apply any pending scroll once the iframe has loaded
  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);
    const pending = pendingScrollRef.current;
    if (pending && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(pending, window.location.origin);
      pendingScrollRef.current = null;
    }
  }, []);

  // Auto-scroll to a specific finding when the panel opens pre-targeted
  useEffect(() => {
    if (!scrollToFindingUuid) return;
    const payload = {
      type: 'scrollToFinding' as const,
      findingUuid: scrollToFindingUuid,
      searchText: scrollSearchText || '',
      sectionReference: scrollSectionReference || '',
      targetSubsection: '',
    };
    lastScrollRef.current = payload;
    if (iframeLoaded && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(payload, window.location.origin);
    } else {
      pendingScrollRef.current = payload as any;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToFindingUuid, scrollNonce]);

  /* Apply the reader's size - on every change, and once the frame is up. */
  useEffect(() => {
    if (!readingScalePx) return;
    const frame = iframeRef.current;
    if (!frame?.contentWindow || !iframeLoaded) return;
    frame.contentWindow.postMessage({ type: 'setReadingScale', px: readingScalePx }, window.location.origin);
  }, [readingScalePx, iframeLoaded, activeDocxHtml]);

  /**
   * A clause was clicked in the document.
   *
   * This `srcDoc` iframe carries NO `sandbox` attribute, so per spec its
   * document inherits the embedder's origin - it is same-origin, and `e.origin`
   * equals `window.location.origin` (not the string "null" that a sandboxed
   * frame would report). We verify both: `e.origin` against our own origin
   * (rejects any cross-origin sender) AND `e.source` against our own iframe's
   * contentWindow (rejects any other same-origin window). The source check is
   * the stronger identity; the origin check is defense-in-depth.
   */
  useEffect(() => {
    if (!onFindingSelect) return;
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.source !== iframeRef.current?.contentWindow) return;
      if (!e.data || e.data.type !== 'findingClicked') return;
      if (typeof e.data.findingUuid === 'string') onFindingSelect(e.data.findingUuid);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onFindingSelect]);

  /*
    New html - a new stream of this document, or its revision re-rendered after
    a decision. The frame is about to remount, so `iframeLoaded` goes back to
    false and the scroll target is RE-ARMED rather than dropped: `handleIframeLoad`
    fires it at the fresh frame, which is what carries the reader back to the
    clause they were on. Dropping it (as this did) is why an accepted finding
    reloaded the document to the top with nothing marked.
  */
  useEffect(() => {
    setIframeLoaded(false);
    pendingScrollRef.current = lastScrollRef.current;
  }, [docxHtml, revisedDocxHtml]);


  const loading = isLoading || activeLoading;

  // Which formats can be revised is one rule, shared with the toolbar.
  const supportsRevision = useMemo(
    () => fileSupportsRevision(documentContent?.file_type),
    [documentContent?.file_type],
  );

  return (
    <div className="flex flex-col w-full h-full">
      {/* Toolbar - hidden when header is managed by parent panel */}
      {!hideHeader && (
      <div className="flex-shrink-0 flex items-center justify-between px-4 pt-3 pb-3 border-b border-gray-100 dark:border-gray-700/60" data-tour-id="doc-viewer-toolbar">
        {/* Left: metadata + source toggle */}
        <div className="flex items-center gap-4">
          {documentContent && (
            <span className="text-xs text-gray-400">
              {documentContent.file_type?.toUpperCase()}
              {documentContent.page_count ? ` · ${documentContent.page_count} pages` : ''}
            </span>
          )}

          {/* Original / Revised toggle - subtle pill */}
          {supportsRevision && (
            <div className="flex items-center rounded-full bg-gray-100 dark:bg-gray-800 p-0.5">
              {(['original', 'revised'] as ViewSource[]).map((src) => (
                <button
                  key={src}
                  onClick={() => setViewSource(src)}
                  className={clsx(
                    'px-3 py-1 text-xs font-medium rounded-full transition-all duration-200',
                    viewSource === src
                      ? 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
                  )}
                >
                  {src === 'original' ? t('Original') : t('Revised')}
                </button>
              ))}
            </div>
          )}

          {/* Clean / Redline sub-toggle - subtle pill */}
          {supportsRevision && viewSource === 'revised' && (
            <div className="flex items-center rounded-full bg-gray-100 dark:bg-gray-800 p-0.5">
              {(['clean', 'redline'] as RevisionStyle[]).map((style) => (
                <button
                  key={style}
                  onClick={() => setRevisionStyle(style)}
                  className={clsx(
                    'px-3 py-1 text-xs font-medium rounded-full transition-all duration-200',
                    revisionStyle === style
                      ? 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
                  )}
                >
                  {style === 'clean' ? t('Clean') : t('Redline')}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: download */}
        <div className="flex items-center gap-2">
          {(activeBlobUrl || blobUrl) && (
            <button
              onClick={handleDownload}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title={t('Download')}
            >
              <PiDownloadSimpleLight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      )}

      {/* Document body */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Document viewer */}
        <div className="flex-1 min-w-0 flex flex-col bg-white dark:bg-gray-900" data-tour-id="doc-viewer-content">
          {loading ? (
            <div className="flex-1 p-8 space-y-3 overflow-hidden">
              <div className="h-4 w-1/3 rounded-md bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
              <div className="h-3 w-full rounded-md bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
              <div className="h-3 w-5/6 rounded-md bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
              <div className="h-3 w-4/5 rounded-md bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
              <div className="h-3 w-full rounded-md bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
              <div className="mt-5 h-4 w-1/4 rounded-md bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
              <div className="h-3 w-full rounded-md bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
              <div className="h-3 w-11/12 rounded-md bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
              <div className="h-3 w-4/5 rounded-md bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
              <div className="h-3 w-full rounded-md bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
              <div className="h-3 w-3/4 rounded-md bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
              <div className="mt-5 h-4 w-2/5 rounded-md bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
              <div className="h-3 w-full rounded-md bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
              <div className="h-3 w-5/6 rounded-md bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
              <div className="h-3 w-full rounded-md bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
            </div>
          ) : viewSource === 'revised' && noRevisionAvailable ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
              <PiArrowsClockwiseLight className="w-14 h-14" />
              <p className="text-sm font-medium">{t('No Revised Version Yet')}</p>
              <p className="text-xs text-gray-400 max-w-sm text-center">
                {t('Accept or modify findings to generate a revised document with changes applied.')}
              </p>
            </div>
          ) : activeError || (!activeBlobUrl && !activeDocxHtml) ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <PiFileTextLight className="w-16 h-16 mb-4" />
              <p className="text-sm">{t('Unable to load document')}</p>
            </div>
          ) : viewerType === 'docx' && activeDocxHtml ? (
            <div className="flex-1 bg-white dark:bg-gray-900 overflow-hidden">
              <iframe
                ref={iframeRef}
                title={documentName}
                srcDoc={activeDocxHtml}
                className="border-0 w-full h-full bg-white"
                onLoad={handleIframeLoad}
              />
            </div>
          ) : viewerType === 'pdf' ? (
            <iframe
              src={activeBlobUrl as string}
              className="w-full flex-1 border-0"
              title={documentName}
            />
          ) : viewerType === 'image' ? (
            <div className="flex items-center justify-center h-full p-8 overflow-auto">
              <img
                src={activeBlobUrl as string}
                alt={documentName}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          ) : viewerType === 'text' ? (
            <iframe
              src={activeBlobUrl as string}
              className="w-full flex-1 border-0"
              title={documentName}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-4">
              <PiFileTextLight className="w-16 h-16" />
              <p className="text-sm">{t('This file type cannot be previewed directly.')}</p>
              <button
                onClick={handleDownload}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                <PiDownloadSimpleLight className="w-4 h-4 inline me-1.5" />
                {t('Download Document')}
              </button>
            </div>
          )}
        </div>

      </div>

      <FeatureTour
        steps={documentViewerTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
};

export default DocumentViewerModal;
